/**
 * Opening-hours helpers.
 *
 * locations.opening_hours is { monday: { open: "09:00", close: "18:00" }, ... }
 * in the merchant's wall-clock time; a missing day means closed.
 */

import { asZoned, zonedViewToUtc } from './timezone';

const DAY_NAMES = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
const TIME_RE = /^\d{1,2}:\d{2}$/;

export interface ClosingPeriodLike {
  start_date: string;
  end_date: string;
}

type OpeningHours = Record<string, { open?: string; close?: string } | null>;

function ymdOf(zoned: Date): string {
  return [
    zoned.getFullYear(),
    String(zoned.getMonth() + 1).padStart(2, '0'),
    String(zoned.getDate()).padStart(2, '0'),
  ].join('-');
}

function inClosingPeriod(zonedDay: Date, closingPeriods: ClosingPeriodLike[] | null | undefined): boolean {
  const ymd = ymdOf(zonedDay);
  return (closingPeriods || []).some(
    (p) => p.start_date.slice(0, 10) <= ymd && ymd <= p.end_date.slice(0, 10)
  );
}

function hoursFor(openingHours: unknown, zonedDay: Date) {
  return ((openingHours as OpeningHours | null) || {})[DAY_NAMES[zonedDay.getDay()]] || null;
}

/**
 * Closing time on the merchant-local day containing `at`, as a UTC Date, or
 * null when there is none to use: closed that day (no hours, or inside a
 * closing period), malformed hours, or closing time is not after `at`.
 *
 * "00:00" / "24:00" as a close means end of day (midnight after that day),
 * matching the new-booking wizard.
 */
export function closingTimeOnDayOf(
  openingHours: unknown,
  closingPeriods: ClosingPeriodLike[] | null | undefined,
  timeZone: string,
  at: Date = new Date()
): Date | null {
  const zonedAt = asZoned(at, timeZone);
  if (inClosingPeriod(zonedAt, closingPeriods)) return null;

  const close = hoursFor(openingHours, zonedAt)?.close;
  if (!close || !TIME_RE.test(close)) return null;

  const [h, m] = close.split(':').map((v) => parseInt(v, 10));
  const zonedClose = new Date(zonedAt.getTime());
  if ((h === 0 && m === 0) || (h === 24 && m === 0)) {
    zonedClose.setHours(0, 0, 0, 0);
    zonedClose.setDate(zonedClose.getDate() + 1);
  } else {
    zonedClose.setHours(h, m, 0, 0);
  }

  const closeUtc = zonedViewToUtc(zonedClose, timeZone);
  if (isNaN(closeUtc.getTime()) || closeUtc.getTime() <= at.getTime()) return null;
  return closeUtc;
}

/** Today's closing time (see closingTimeOnDayOf). */
export function closingTimeToday(
  openingHours: unknown,
  closingPeriods: ClosingPeriodLike[] | null | undefined,
  timeZone: string,
  now: Date = new Date()
): Date | null {
  return closingTimeOnDayOf(openingHours, closingPeriods, timeZone, now);
}

/**
 * The next opening time strictly after `after` (later today if the location
 * hasn't opened yet), skipping closed days and closing periods. Looks at most
 * `maxDays` days ahead; null when nothing is found.
 */
export function nextOpeningTime(
  openingHours: unknown,
  closingPeriods: ClosingPeriodLike[] | null | undefined,
  timeZone: string,
  after: Date = new Date(),
  maxDays = 14
): Date | null {
  const zonedAfter = asZoned(after, timeZone);
  for (let i = 0; i <= maxDays; i++) {
    const zonedDay = new Date(zonedAfter.getTime());
    zonedDay.setDate(zonedDay.getDate() + i);
    if (inClosingPeriod(zonedDay, closingPeriods)) continue;

    const open = hoursFor(openingHours, zonedDay)?.open;
    if (!open || !TIME_RE.test(open)) continue;

    const [h, m] = open.split(':').map((v) => parseInt(v, 10));
    zonedDay.setHours(h, m, 0, 0);
    const openUtc = zonedViewToUtc(zonedDay, timeZone);
    if (!isNaN(openUtc.getTime()) && openUtc.getTime() > after.getTime()) return openUtc;
  }
  return null;
}
